import { useCallback, useEffect } from "react";
import type { SelectOption } from "../SelectField";

// Torne o tipo Shortcuts exportável para reuso externo
export type DropdownNavigationShortcuts = Partial<Record<
  "ArrowDown" | "ArrowUp" | "Home" | "End" | "PageUp" | "PageDown" | "Enter" | " " | "Escape",
  (e: React.KeyboardEvent, ctx: { open: boolean; highlighted: number; options: SelectOption[] }) => void
>>;

// Para compatibilidade, mantenha o nome antigo como alias interno (opcional)
type Shortcuts = DropdownNavigationShortcuts;

export function useDropdownNavigation(
  isDropdownOpen: boolean,
  setIsDropdownOpen: (open: boolean) => void,
  options: SelectOption[],
  highlightedIndex: number,
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>,
  handleOptionSelect: (value: string) => void,
  multiple: boolean,
  value: string | string[],
  onChange: (value: string | string[]) => void,
  searchQuery: string,
  keyboardShortcuts?: DropdownNavigationShortcuts // use o tipo exportado aqui
) {
  // Resetar o índice ao digitar uma nova busca
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchQuery, setHighlightedIndex]);
  
  return useCallback(
    (e: React.KeyboardEvent) => {
      const ctx = { open: isDropdownOpen, highlighted: highlightedIndex, options };
      if (keyboardShortcuts?.[e.key as keyof Shortcuts]) {
        keyboardShortcuts[e.key as keyof Shortcuts]?.(e, ctx);
        return;
      }
      if (!isDropdownOpen) {
        if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
          setIsDropdownOpen(true);
          e.preventDefault();
        }
        return;
      }
      switch (e.key) {
        case "ArrowDown":
            setHighlightedIndex((prev: number) => Math.min(prev + 1, options.length - 1));
          e.preventDefault();
          break;
        case "ArrowUp":
            setHighlightedIndex((prev: number) => Math.max(prev - 1, 0));
          e.preventDefault();
          break;
        case "Home":
          setHighlightedIndex(0);
          e.preventDefault();
          break;
        case "End":
          setHighlightedIndex(options.length - 1);
          e.preventDefault();
          break;
        case "PageDown":
            setHighlightedIndex((prev: number) => Math.min(prev + 5, options.length - 1));
          e.preventDefault();
          break;
        case "PageUp":
            setHighlightedIndex((prev: number) => Math.max(prev - 5, 0));
            e.preventDefault();
            break;
        case "Enter":
        case " ":
          if (highlightedIndex >= 0 && options[highlightedIndex]) {
            if (multiple) {
              // Alterna seleção no modo múltiplo
              const selectedValue = options[highlightedIndex].value;
              let newValue: string[];
              if (Array.isArray(value)) {
                if (value.includes(selectedValue)) {
                  newValue = value.filter((v) => v !== selectedValue);
                } else {
                  newValue = [...value, selectedValue];
                }
              } else {
                newValue = [selectedValue];
              }
              onChange(newValue);
            } else {
              handleOptionSelect(options[highlightedIndex].value);
              setIsDropdownOpen(false);
            }
            e.preventDefault();
          }
          break;
        case "Escape":
          setIsDropdownOpen(false);
          e.preventDefault();
          break;
        default:
          // Navegação por letra inicial com wrap-around
          if (e.key.length === 1 && /^[a-zA-Z0-9]$/.test(e.key)) {
            let idx = options.findIndex(
              (opt, i) =>
                i > highlightedIndex &&
                opt.label.toLowerCase().startsWith(e.key.toLowerCase())
            );
            if (idx === -1) {
              idx = options.findIndex(
                (opt, i) =>
                  i >= 0 &&
                  opt.label.toLowerCase().startsWith(e.key.toLowerCase())
              );
            }
            if (idx !== -1) {
              setHighlightedIndex(idx);
              e.preventDefault();
            }
          }
          break;
      }
    },
    [
      isDropdownOpen,
      setIsDropdownOpen,
      options,
      highlightedIndex,
      setHighlightedIndex,
      handleOptionSelect,
      keyboardShortcuts,
      multiple,
      value,
      onChange,
    ]
  );
}